import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AuditEntry = ({ entry, onViewDetails, className = '' }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getActivityIcon = (type) => {
    switch (type) {
      case 'test_result': return 'FlaskConical';
      case 'equipment_calibration': return 'Settings';
      case 'adverse_event': return 'Shield';
      case 'regulatory_submission': return 'FileText';
      case 'batch_approval': return 'CheckCircle';
      case 'document_update': return 'Edit';
      case 'user_access': return 'User';
      default: return 'Activity';
    }
  };

  const getVerificationStatus = (status) => {
    switch (status) {
      case 'verified':
        return {
          color: 'text-success',
          bgColor: 'bg-success/10',
          icon: 'CheckCircle',
          label: 'Verified'
        };
      case 'pending':
        return {
          color: 'text-warning',
          bgColor: 'bg-warning/10',
          icon: 'Clock',
          label: 'Pending'
        };
      case 'failed':
        return {
          color: 'text-error',
          bgColor: 'bg-error/10',
          icon: 'AlertCircle',
          label: 'Failed'
        };
      default:
        return {
          color: 'text-muted-foreground',
          bgColor: 'bg-muted',
          icon: 'Circle',
          label: 'Unknown'
        };
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'border-l-error';
      case 'high': return 'border-l-warning';
      case 'medium': return 'border-l-primary';
      case 'low': return 'border-l-success';
      default: return 'border-l-muted';
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp)?.toLocaleString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZone: 'Europe/Athens'
    });
  };

  const truncateHash = (hash) => {
    return `${hash?.substring(0, 8)}...${hash?.substring(hash?.length - 8)}`;
  };

  const verificationStatus = getVerificationStatus(entry?.verificationStatus);

  return (
    <div className={`bg-card border border-border rounded-lg hover:shadow-sm transition-shadow duration-150 ${className}`}>
      {/* Main Entry */}
      <div className={`p-4 border-l-4 ${getSeverityColor(entry?.severity)}`}>
        <div className="flex items-start justify-between">
          <div className="flex items-start space-x-3 flex-1">
            {/* Activity Icon */}
            <div className="flex-shrink-0 mt-1">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon 
                  name={getActivityIcon(entry?.activityType)} 
                  size={16} 
                  className="text-primary"
                />
              </div>
            </div>

            {/* Entry Details */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2 mb-1">
                <h4 className="font-medium text-card-foreground truncate">
                  {entry?.action}
                </h4>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${verificationStatus?.bgColor} ${verificationStatus?.color}`}>
                  <Icon name={verificationStatus?.icon} size={12} className="inline mr-1" />
                  {verificationStatus?.label}
                </span>
              </div>

              <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
                {entry?.description}
              </p>

              <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                <span className="flex items-center space-x-1">
                  <Icon name="User" size={12} />
                  <span>{entry?.user}</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Icon name="Clock" size={12} />
                  <span>{formatTimestamp(entry?.timestamp)}</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Icon name="Hash" size={12} />
                  <span className="font-mono">{truncateHash(entry?.blockchainHash)}</span>
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center space-x-2 ml-4">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1 text-muted-foreground hover:text-foreground transition-colors duration-150"
            >
              <Icon 
                name={isExpanded ? "ChevronUp" : "ChevronDown"} 
                size={16} 
              />
            </button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onViewDetails(entry)}
              iconName="ExternalLink"
              iconPosition="right"
            >
              Details
            </Button>
          </div>
        </div>
      </div>
      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-4 pb-4 border-t border-border bg-muted/20">
          <div className="pt-4 space-y-3">
            {/* Blockchain Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h5 className="text-sm font-medium text-card-foreground mb-2">Blockchain Details</h5>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Block Number:</span>
                    <span className="font-mono text-card-foreground">{entry?.blockNumber}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Transaction Hash:</span>
                    <span className="font-mono text-card-foreground">{truncateHash(entry?.transactionHash)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Gas Used:</span>
                    <span className="font-mono text-card-foreground">{entry?.gasUsed?.toLocaleString()}</span>
                  </div>
                </div>
              </div>

              <div>
                <h5 className="text-sm font-medium text-card-foreground mb-2">Audit Information</h5>
                <div className="space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">User Role:</span>
                    <span className="text-card-foreground capitalize">{entry?.userRole?.replace('_', ' ')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">IP Address:</span>
                    <span className="font-mono text-card-foreground">{entry?.ipAddress}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Session ID:</span>
                    <span className="font-mono text-card-foreground">{truncateHash(entry?.sessionId)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Data Changes */}
            {entry?.dataChanges && entry?.dataChanges?.length > 0 && (
              <div>
                <h5 className="text-sm font-medium text-card-foreground mb-2">Data Changes</h5>
                <div className="bg-card rounded-md p-3 space-y-2">
                  {entry?.dataChanges?.map((change, index) => (
                    <div key={index} className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{change?.field}:</span>
                      <div className="flex items-center space-x-2">
                        <span className="text-error line-through">{change?.oldValue}</span>
                        <Icon name="ArrowRight" size={12} className="text-muted-foreground" />
                        <span className="text-success">{change?.newValue}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center justify-between pt-2">
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Copy"
                  iconPosition="left"
                >
                  Copy Hash
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Shield"
                  iconPosition="left"
                >
                  Verify Integrity
                </Button>
              </div>
              
              <Button
                variant="ghost"
                size="sm"
                iconName="Download"
                iconPosition="left"
              >
                Export Entry
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuditEntry;